import { memo } from "react";
import { Building, MapPin, Check, Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getBookingTheme, BookingTheme } from "@/config/bookingThemes";

interface Location {
  id: string;
  name: string;
  address?: string | null;
  city?: string | null;
  is_primary?: boolean;
}

interface LocationSelectorProps {
  locations: Location[];
  selectedLocationId: string | null;
  onSelect: (locationId: string) => void;
  theme?: BookingTheme;
  className?: string;
}

const LocationCard = memo(({ 
  location, 
  isSelected, 
  onSelect,
  theme,
}: { 
  location: Location; 
  isSelected: boolean; 
  onSelect: () => void;
  theme: BookingTheme;
}) => (
  <Card
    className={cn(
      "group cursor-pointer overflow-hidden transition-all duration-300 touch-manipulation",
      "hover:shadow-xl hover:-translate-y-1 border-2",
      "hover:border-primary/50",
      isSelected && "border-primary ring-2 ring-primary/20"
    )}
    onClick={onSelect}
  >
    {/* Top gradient line */}
    <div className={cn(
      "absolute top-0 left-0 w-full h-1 transition-opacity duration-300",
      `bg-gradient-to-r ${theme.gradient}`,
      isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
    )} />
    
    <CardContent className="p-0">
      {/* Header with gradient background */}
      <div className={cn(
        "h-20 sm:h-24 flex items-center justify-center relative overflow-hidden",
        `bg-gradient-to-br ${theme.gradientLight}`
      )}>
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-2 right-2 w-12 h-12 rounded-full bg-primary/10" />
          <div className="absolute bottom-2 left-2 w-8 h-8 rounded-full bg-primary/10" />
        </div>
        
        <div className={cn(
          "w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center transition-all duration-300",
          "group-hover:scale-110",
          isSelected 
            ? `bg-gradient-to-br ${theme.gradient} shadow-lg`
            : "bg-white/80 backdrop-blur-sm shadow"
        )}>
          <Building className={cn(
            "h-7 w-7 sm:h-8 sm:w-8",
            isSelected ? "text-white" : "text-primary"
          )} />
        </div>
        
        {location.is_primary && (
          <Badge className={cn(
            "absolute top-2 right-2",
            `bg-gradient-to-r ${theme.gradient} border-0`
          )}>
            <Star className="h-3 w-3 mr-1" />
            Principal
          </Badge>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-base truncate">{location.name}</h4>
            {(location.address || location.city) && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
                <span className="truncate">
                  {[location.address, location.city].filter(Boolean).join(", ")}
                </span>
              </p>
            )}
          </div>
          
          {isSelected && (
            <div className={cn(
              "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0",
              `bg-gradient-to-br ${theme.gradient}`
            )}>
              <Check className="h-4 w-4 text-white" />
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
));
LocationCard.displayName = "LocationCard";

export const LocationSelector = memo(function LocationSelector({
  locations,
  selectedLocationId,
  onSelect,
  theme,
  className,
}: LocationSelectorProps) {
  // Default theme if not provided
  const activeTheme = theme || getBookingTheme("trainer");
  const ThemeIcon = activeTheme.icon;

  if (locations.length === 0) {
    return (
      <div className={cn("text-center py-8", className)}>
        <div className={cn(
          "w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4",
          `bg-gradient-to-br ${activeTheme.gradientLight}`
        )}>
          <Building className="h-8 w-8 text-muted-foreground" />
        </div>
        <p className="text-muted-foreground font-medium">No hay ubicaciones disponibles</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <h3 className="font-semibold text-lg flex items-center gap-2">
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center",
          `bg-gradient-to-br ${activeTheme.gradient}`
        )}>
          <MapPin className="h-4 w-4 text-white" />
        </div>
        ¿Dónde quieres entrenar?
      </h3>
      <div className="grid gap-3 sm:grid-cols-2">
        {locations.map((location, i) => (
          <div 
            key={location.id}
            className="animate-slide-up-fade"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <LocationCard
              location={location}
              isSelected={selectedLocationId === location.id}
              onSelect={() => onSelect(location.id)}
              theme={activeTheme}
            />
          </div>
        ))}
      </div>
    </div>
  );
});
